import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import NewAccountModal from '../components/NewAccountModal';
import { useSente, SenteIdentity, SenteApiError } from '../hooks/useSente';
import { OPEN_NEW_IDENTITY_EVENT } from '../onboarding/OnboardingTour';

const Identities: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { ready, listIdentities, createIdentity, deleteIdentity } = useSente();
  const [identities, setIdentities] = useState<SenteIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Create modal state
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLocalPart, setNewLocalPart] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<React.ReactNode>(null);

  // New Account modal (accounts-first: no identity picked — the server auto-provisions one)
  const [showNewAccount, setShowNewAccount] = useState(false);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<SenteIdentity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setIdentities(await listIdentities());
    } catch (e: any) {
      setError(e.message || 'Failed to load identities');
    } finally {
      setIsLoading(false);
    }
  }, [listIdentities]);

  useEffect(() => {
    if (ready) refresh();
  }, [ready, refresh]);

  // The onboarding tour ends here: open the New Identity modal when it finishes.
  useEffect(() => {
    const handler = () => {
      setCreateError(null);
      setShowModal(true);
    };
    window.addEventListener(OPEN_NEW_IDENTITY_EVENT, handler);
    return () => window.removeEventListener(OPEN_NEW_IDENTITY_EVENT, handler);
  }, []);

  const copyEmail = (e: React.MouseEvent, identity: SenteIdentity) => {
    e.stopPropagation();
    navigator.clipboard.writeText(identity.email);
    setCopiedId(identity.id);
    setTimeout(() => setCopiedId(prev => (prev === identity.id ? null : prev)), 1500);
  };

  const handleCreate = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      await createIdentity({
        name: newName.trim() || undefined,
        localPart: newLocalPart.trim() || undefined,
        onConflict: newLocalPart.trim() ? 'error' : undefined,
      });
      setShowModal(false);
      setNewName('');
      setNewLocalPart('');
      refresh();
    } catch (e: any) {
      if (e instanceof SenteApiError && e.code === 'IDENTITY_LIMIT_EXCEEDED') {
        setCreateError(
          <>
            Identity limit reached for your plan.{' '}
            <Link to="/billing" className="underline font-medium">Upgrade</Link>
          </>
        );
      } else if (e instanceof SenteApiError && e.status === 409) {
        setCreateError('That address is taken — pick another or leave blank for a random one.');
      } else {
        setCreateError(e.message || 'Failed to create identity');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const openModal = () => {
    setCreateError(null);
    setShowModal(true);
  };

  const openDelete = (e: React.MouseEvent, identity: SenteIdentity) => {
    e.stopPropagation();
    setDeleteError(null);
    setDeleteTarget(identity);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteIdentity(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
    } catch (e: any) {
      if (e instanceof SenteApiError && e.status === 409 && e.code === 'IDENTITY_BUSY') {
        setDeleteError('This identity has a run in progress — wait for it to finish.');
      } else {
        setDeleteError(e.message || 'Failed to delete identity');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="h-full overflow-hidden flex flex-col bg-white dark:bg-slate-950">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs items={[{ label: 'Identities' }]} className="mb-6" />

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Identities</h1>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Email identities your agents own — send, receive, and react to mail.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-tour="new-identity"
                    onClick={openModal}
                    className="px-6 py-3 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl font-semibold transition-colors"
                  >
                    New Identity
                  </button>
                  {/* Accounts-first: the primary CTA — no identity needed, the server provisions one. */}
                  <button
                    onClick={() => setShowNewAccount(true)}
                    className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 dark:from-blue-600 dark:to-purple-600 dark:hover:from-blue-500 dark:hover:to-purple-500 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-gray-500/25 dark:shadow-blue-500/25 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 4v16m8-8H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>New Account</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Loading */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : identities.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800">
                <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No identities yet</h3>
                <p className="text-gray-500 dark:text-slate-400 mb-4">
                  Create an email identity your agent owns — it can send, receive, and react to mail.
                </p>
                <button
                  onClick={openModal}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  New Identity
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {identities.map(identity => (
                  <div
                    key={identity.id}
                    onClick={() => navigate(`/identities/${identity.id}`)}
                    className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {identity.name || identity.email.split('@')[0]}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                          <span className="font-mono truncate">{identity.email}</span>
                          <button
                            onClick={e => copyEmail(e, identity)}
                            className="p-1 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors flex-shrink-0"
                            title="Copy email"
                          >
                            {copiedId === identity.id ? (
                              <span className="text-xs text-green-600 dark:text-green-400 px-1">Copied</span>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                        <span className="text-xs text-gray-500 dark:text-slate-500">
                          Created {new Date(identity.createdAt).toLocaleDateString()}
                        </span>
                        <button
                          onClick={e => openDelete(e, identity)}
                          className="p-1 text-gray-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors flex-shrink-0"
                          title="Delete identity"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !isCreating && setShowModal(false)}>
          <div
            className="w-full max-w-md p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">New Identity</h2>

            {createError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {createError}
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Name <span className="text-gray-400 dark:text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Support Agent"
              className="w-full mb-4 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Custom address <span className="text-gray-400 dark:text-slate-500 font-normal">(optional — random if blank)</span>
            </label>
            <div className="flex items-center mb-4">
              <input
                type="text"
                value={newLocalPart}
                onChange={e => setNewLocalPart(e.target.value)}
                placeholder="my-agent"
                className="flex-1 min-w-0 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-l-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="px-3 py-2 bg-gray-100 dark:bg-slate-800 border border-l-0 border-gray-300 dark:border-slate-700 rounded-r-lg text-sm text-gray-500 dark:text-slate-400 whitespace-nowrap">
                @sente.run
              </span>
            </div>

            {/* Phone is a reserved identity channel (SMS verification) — not yet available. */}
            <label className="flex items-center gap-2 text-sm font-medium text-gray-400 dark:text-slate-500 mb-1">
              Phone number
              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400">
                Coming soon
              </span>
            </label>
            <input
              type="tel"
              disabled
              placeholder="SMS verification — coming soon"
              title="Phone numbers (SMS verification) are coming soon"
              className="w-full mb-6 px-3 py-2 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-lg text-gray-400 dark:text-slate-500 placeholder-gray-400 dark:placeholder-slate-600 cursor-not-allowed"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                disabled={isCreating}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreating}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
              >
                {isCreating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !isDeleting && setDeleteTarget(null)}>
          <div
            className="w-full max-w-md p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Delete Identity</h2>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {deleteError}
              </div>
            )}

            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
              Delete <span className="font-mono text-gray-900 dark:text-white">{deleteTarget.email}</span>? Its inbox,
              registrations, and credentials will be permanently deleted.
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Account without an identity — connect-led; the backing identity is auto-provisioned. */}
      <NewAccountModal open={showNewAccount} onClose={() => setShowNewAccount(false)} initialMode="connect" />
    </div>
  );
};

export default Identities;
